import React from 'react';
import dayjs from 'dayjs';
import { Button, buttonVariants } from '@/design-system/button';
import { DatePicker } from '@/components/DatePicker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/design-system/popover';
import { SearchField } from '@/design-system/search-field';
import { SearchSelect } from '@/design-system/search-select';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';

/**
 * Relative windows, which is how Ops actually asks the question ("what came in
 * this week"), not as absolute dates. `all` is present because the buckets are
 * lifecycle states, not time buckets: an order stuck in `outstanding` for two
 * months is exactly the row you must not hide behind a 30-day default.
 */
export const DATE_PRESETS = [
  '1d',
  '7d',
  '30d',
  '90d',
  'all',
  'custom',
] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

/** The one-click windows. `custom` is excluded — it has its own control. */
const RELATIVE_PRESETS = DATE_PRESETS.filter(
  (preset) => preset !== 'custom'
) as Exclude<DatePreset, 'custom'>[];

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  '1d': '1 hari',
  '7d': '7 hari',
  '30d': '30 hari',
  '90d': '90 hari',
  all: 'Semua',
  custom: 'Custom',
};

/**
 * A preset → the `startDate` / `endDate` the API takes.
 *
 * Both are plain `YYYY-MM-DD`: the backend compares them against the date
 * column AT TIME ZONE <client tz>, so sending an instant would re-open the
 * timezone question the server already answers. Which column — created or
 * completed — is the caller's mapping, not this helper's.
 */
export const rangeForPreset = (
  preset: DatePreset,
  custom: { start: string; end: string }
): { startDate?: string; endDate?: string } => {
  if (preset === 'all') return {};
  if (preset === 'custom') {
    return {
      startDate: custom.start || undefined,
      endDate: custom.end || undefined,
    };
  }
  const days = Number(preset.replace('d', ''));
  return {
    startDate: dayjs()
      .subtract(days - 1, 'day')
      .format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  };
};

/**
 * The applied range, as the Custom button's own label.
 *
 * A filter row that says "Custom" tells the reader nothing about what is being
 * filtered — the point of the button is to carry the answer. The year is
 * printed once when both ends share it, because `1 Jun 2026 – 30 Jun 2026`
 * spends half its width saying 2026 twice.
 */
const formatAppliedRange = (from: string, to: string): string => {
  if (!from || !to) return 'Custom';
  const start = dayjs(from, 'YYYY-MM-DD');
  const finish = dayjs(to, 'YYYY-MM-DD');
  if (!start.isValid() || !finish.isValid()) return 'Custom';
  if (start.isSame(finish, 'day')) return start.format('D MMM YYYY');
  return start.isSame(finish, 'year')
    ? `${start.format('D MMM')} – ${finish.format('D MMM YYYY')}`
    : `${start.format('D MMM YYYY')} – ${finish.format('D MMM YYYY')}`;
};

interface AppliedRange {
  from: string;
  to: string;
}

/**
 * Custom date range: two inputs in a popover, committed only on Terapkan.
 *
 * The draft lives in LOCAL state and nothing reaches the URL until the
 * operator applies it. That is the whole design: writing each keystroke
 * straight to the URL meant every step of picking a date — scrolling to the
 * month, then the day, then the second date — was its own filter change and
 * its own request, so the operator watched the table churn through ranges they
 * never asked for. Two dates now cost one fetch.
 *
 * Dismissing the popover (outside click / Escape) is therefore a cancel, and
 * the draft is re-seeded from the applied value on every open so a cancelled
 * edit leaves nothing behind.
 */
const CustomRangePopover: React.FC<{
  applied: AppliedRange;
  active: boolean;
  onApply: (range: AppliedRange) => void;
}> = ({ applied, active, onApply }) => {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<AppliedRange>(applied);

  const handleOpenChange = (next: boolean) => {
    // Re-seed on open, not on mount: `applied` can change underneath (another
    // preset, a linked URL) while this component stays mounted.
    if (next) setDraft(applied);
    setOpen(next);
  };

  // A bounded range is what this control means; "from X onwards" is what the
  // Semua preset is for. Requiring both also keeps the button's label honest.
  const canApply = Boolean(draft.from && draft.to);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {/* `PopoverTrigger` renders the <button> itself, dressed in the Button
          kit's own variant classes — deliberately NOT `asChild` around a
          `<Button>`.

          Radix's Slot treats `style` as a CSS object and spreads it, while the
          kit's Button takes `style` as its VARIANT name ("filled" / "stroke",
          see its `Omit<…, "style">`). Through a Slot the string is spread
          character by character, the cva lookup misses, and the trigger loses
          every variant class: it rendered as bare text next to the other
          preset buttons. Using `buttonVariants` directly keeps one source of
          truth for the styling and a real button for the aria wiring. */}
      <PopoverTrigger
        className={buttonVariants({
          size: 'xs',
          style: active ? 'filled' : 'stroke',
          tone: 'neutral',
        })}
      >
        {active ? formatAppliedRange(applied.from, applied.to) : 'Custom'}
      </PopoverTrigger>

      <PopoverContent align="start" className="w-auto p-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-text-sub-600">Dari</span>
              <DatePicker
                className="w-[160px]"
                value={draft.from}
                // Cross-bounded, so an inverted range is not selectable at all
                // rather than being rejected after the fact.
                maxDate={draft.to || undefined}
                placeholder="Pilih tanggal"
                onChange={(iso) =>
                  setDraft((prev) => ({ ...prev, from: toIsoDate(iso) }))
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-text-sub-600">Sampai</span>
              <DatePicker
                className="w-[160px]"
                value={draft.to}
                minDate={draft.from || undefined}
                placeholder="Pilih tanggal"
                onChange={(iso) =>
                  setDraft((prev) => ({ ...prev, to: toIsoDate(iso) }))
                }
              />
            </label>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-stroke-soft-200 pt-3">
            <span className="text-xs text-text-sub-600">
              {canApply
                ? formatAppliedRange(draft.from, draft.to)
                : 'Pilih tanggal Dari dan Sampai'}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="xs"
                style="ghost"
                tone="neutral"
                onClick={() => setOpen(false)}
              >
                Batal
              </Button>
              <Button
                size="xs"
                disabled={!canApply}
                onClick={() => {
                  onApply(draft);
                  setOpen(false);
                }}
              >
                Terapkan
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

/**
 * The shared DatePicker's ISO output → the `YYYY-MM-DD` the URL and API carry.
 *
 * It encodes the picked calendar day as UTC midnight
 * (`Date.UTC(y, m, d)` → `2026-08-01T00:00:00.000Z`), so the date part of that
 * string IS the day the operator clicked. Taken verbatim rather than re-parsed
 * into a local `Date`, which is exactly how a picked date turns into the day
 * before for anyone west of Greenwich.
 */
const toIsoDate = (iso?: string): string => (iso ? iso.slice(0, 10) : '');

/** One date filter's state: a preset, plus the bounds when it is `custom`. */
export interface DateRangeValue {
  preset: DatePreset;
  customStart: string;
  customEnd: string;
}

/**
 * The completion-date filter: a label, the preset buttons, and its own Custom
 * editor.
 *
 * Shared by the board's bar and the Finance tab, so both read the same date the
 * same way. The label is there because the date it filters is not the obvious
 * one — it is when the delivery completed, not when the order was created.
 */
export const DateRangeFilter: React.FC<{
  label: string;
  value: DateRangeValue;
  onChange: (patch: Partial<DateRangeValue>) => void;
  children?: React.ReactNode;
}> = ({ label, value, onChange, children }) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="shrink-0 text-xs text-text-sub-600">{label}</span>
    {RELATIVE_PRESETS.map((preset) => {
      const active = value.preset === preset;
      return (
        <Button
          key={preset}
          size="xs"
          style={active ? 'filled' : 'stroke'}
          tone="neutral"
          onClick={() => onChange({ preset })}
        >
          {DATE_PRESET_LABELS[preset]}
        </Button>
      );
    })}

    {/* `custom` is not a plain preset button: it carries its own editor and
        its own label, so it is rendered separately rather than squeezed into
        the map above. */}
    <CustomRangePopover
      applied={{ from: value.customStart, to: value.customEnd }}
      active={value.preset === 'custom'}
      onApply={(range) =>
        // One patch, one fetch: preset, from and to together.
        onChange({
          preset: 'custom',
          customStart: range.from,
          customEnd: range.to,
        })
      }
    />

    {children}
  </div>
);

export interface CodFiltersValue {
  search: string;
  clientId?: number;
  /**
   * The board's only date filter: when the delivery completed. A row with no
   * completion date (still in delivery, returned, failed) is excluded while a
   * range is on — `Semua` is what shows them.
   */
  completed: DateRangeValue;
}

/** A change from the bar. The date filter's patch is partial, like the rest. */
export interface CodFiltersPatch {
  search?: string;
  clientId?: number;
  completed?: Partial<DateRangeValue>;
}

const CodFilters: React.FC<{
  value: CodFiltersValue;
  onChange: (patch: CodFiltersPatch) => void;
  onExport: () => void;
  exporting: boolean;
  /** Rows currently matching the filters — the export's size, shown up front. */
  exportCount: number;
  /**
   * Whether the export control belongs on the surface using this bar.
   *
   * The rider recap has nothing to export — its rows are an aggregate the CSV
   * writer knows nothing about — and the row count beside the button counts
   * BOARD rows, so on that tab it reported a number describing a different
   * table. Both hide together: the count is the export's size, so it makes no
   * sense without it.
   */
  showExport?: boolean;
}> = ({
  value,
  onChange,
  onExport,
  exporting,
  exportCount,
  showExport = true,
}) => {
  // `true` keeps the provider list loaded: this is a page-level filter bar, not
  // a modal, so there is no open/close event to hang the first fetch off.
  const providers = useProvidersDropdown(true);

  return (
    <div className="flex flex-col gap-3">
      <DateRangeFilter
        label="Tanggal selesai"
        value={value.completed}
        onChange={(completed) => onChange({ completed })}
      >
        {showExport && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-text-sub-600">
              {exportCount} baris
            </span>
            <Button
              size="xs"
              style="stroke"
              tone="neutral"
              onClick={onExport}
              disabled={exporting || exportCount === 0}
            >
              {exporting ? 'Menyiapkan…' : 'Export CSV'}
            </Button>
          </div>
        )}
      </DateRangeFilter>

      <div className="flex flex-wrap items-center gap-3">
        <SearchField
          value={value.search}
          onChange={(next) => onChange({ search: next })}
          placeholder="Cari order id, waybill, rider, owner…"
          className="w-full max-w-sm"
        />

        <div className="w-full max-w-xs">
          <SearchSelect
            options={providers.options.map((p) => ({
              value: String(p.id),
              label: p.name,
            }))}
            value={value.clientId != null ? String(value.clientId) : undefined}
            selectedLabelFallback={
              value.clientId != null
                ? providers.labelFor(value.clientId)
                : undefined
            }
            onChange={(next) =>
              onChange({ clientId: next ? Number(next) : undefined })
            }
            placeholder="Semua client"
            allowClear
            loading={providers.loading}
            hasMore={providers.hasMore}
            disableLocalFilter
            onSearchChange={providers.setSearch}
            onLoadMore={providers.loadMore}
          />
        </div>
      </div>
    </div>
  );
};

export default CodFilters;
